// ADR-012 i18n — "common" cluster label catalog. Spread into the central LABELS
// in labels.ts. EN values MUST equal the screen's current hardcoded English so
// existing text-based tests stay green; HE values are the Hebrew translation.
export const COMMON_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  LoginPage
  // ═══════════════════════════════════════════════════════════════════════
  loginTitle: { en: 'Admin Portal Login', he: 'כניסה לפורטל הניהול' },
  loginTenantId: { en: 'Tenant ID', he: 'מזהה ארגון' },
  loginTenantPlaceholder: { en: 'e.g. your-company', he: 'לדוגמה: your-company' },
  loginEmail: { en: 'Email', he: 'אימייל' },
  loginPassword: { en: 'Password', he: 'סיסמה' },
  loginSigningIn: { en: 'Signing in...', he: 'מתחבר...' },
  loginSignIn: { en: 'Sign In', he: 'התחברות' },
  loginHandoffExpired: {
    en: 'That sign-in link expired. Please sign in again.',
    he: 'קישור הכניסה פג תוקף. נא להתחבר שוב.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  HandoffPage (sign-in from the marketing site)
  // ═══════════════════════════════════════════════════════════════════════
  handoffSigningIn: { en: 'Signing you in...', he: 'מחבר אותך...' },

  // ═══════════════════════════════════════════════════════════════════════
  //  ChangePasswordPage
  // ═══════════════════════════════════════════════════════════════════════
  changePwMinLength: {
    en: 'New password must be at least {n} characters.',
    he: 'הסיסמה החדשה חייבת להכיל לפחות {n} תווים.',
  },
  changePwMismatch: {
    en: 'New password and confirmation do not match.',
    he: 'הסיסמה החדשה והאישור אינם תואמים.',
  },
  changePwSameAsTemp: {
    en: 'New password must be different from your temporary password.',
    he: 'הסיסמה החדשה חייבת להיות שונה מהסיסמה הזמנית.',
  },
  changePwTitle: { en: 'Set a new password', he: 'הגדרת סיסמה חדשה' },
  changePwSubtitle: {
    en: 'Your account uses a temporary password. Choose a new one to continue.',
    he: 'החשבון שלך משתמש בסיסמה זמנית. בחר סיסמה חדשה כדי להמשיך.',
  },
  changePwAccount: { en: 'Account', he: 'חשבון' },
  changePwNewPassword: { en: 'New password', he: 'סיסמה חדשה' },
  // The default password policy, shown up front (same wording as agent-console). English in both
  // languages by operator decision. Static text of the DEFAULT `Identity.Password.*` policy; the
  // server's own rejection message is still shown after submit if the policy has been changed.
  changePwRules: {
    en: 'At least 8 characters, with an uppercase letter, a number and a special character.',
    he: 'At least 8 characters, with an uppercase letter, a number and a special character.',
  },
  changePwConfirmPassword: { en: 'Confirm new password', he: 'אישור סיסמה חדשה' },
  // Eye toggle on password fields (same wording as agent-console). English in both languages by
  // operator decision, like the rules hint.
  passwordShow: { en: 'Show password', he: 'Show password' },
  passwordHide: { en: 'Hide password', he: 'Hide password' },
  changePwSaving: { en: 'Saving...', he: 'שומר...' },
  changePwSubmit: { en: 'Set new password', he: 'הגדר סיסמה חדשה' },
  changePwBackToLogin: { en: 'Back to login', he: 'חזרה להתחברות' },

  // ═══════════════════════════════════════════════════════════════════════
  //  DashboardPage
  // ═══════════════════════════════════════════════════════════════════════
  dashTitle: { en: 'Dashboard', he: 'לוח בקרה' },
  dashWelcome: { en: 'Welcome', he: 'ברוך הבא' },
  dashRolePlatformAdmin: { en: 'Platform Administrator', he: 'מנהל פלטפורמה' },
  dashRoleAdmin: { en: 'Tenant Administrator', he: 'מנהל ארגון' },
  dashRoleSeniorAgent: { en: 'Senior Agent', he: 'נציג בכיר' },
  dashRoleAgent: { en: 'Agent', he: 'נציג' },
  dashDeveloperSuffix: { en: ' · Developer', he: ' · מפתח' },
  dashOrganization: { en: 'Organization', he: 'ארגון' },
  dashYourOrganization: { en: 'Your organization', he: 'הארגון שלך' },
  dashTenantSwitcherHint: {
    en: 'Use the tenant switcher in the header to scope data',
    he: 'השתמש בבורר הארגונים בכותרת כדי לסנן נתונים',
  },
  dashQuickLinks: { en: 'Quick Links', he: 'קישורים מהירים' },
  dashManageTenants: { en: 'Manage Tenants', he: 'ניהול ארגונים' },

  // ═══════════════════════════════════════════════════════════════════════
  //  NotFoundPage
  // ═══════════════════════════════════════════════════════════════════════
  notFoundMessage: { en: 'Page not found', he: 'הדף לא נמצא' },
  notFoundBack: { en: 'Back to Dashboard', he: 'חזרה ללוח הבקרה' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Forbidden (title reuses CORE forbiddenTitle)
  // ═══════════════════════════════════════════════════════════════════════
  forbiddenDefaultMessage: {
    en: "You don't have access to this page.",
    he: 'אין לך גישה לדף זה.',
  },
  forbiddenBack: { en: 'Back to dashboard', he: 'חזרה ללוח הבקרה' },

  // ═══════════════════════════════════════════════════════════════════════
  //  ErrorBoundary (title reuses CORE errorBoundaryTitle)
  // ═══════════════════════════════════════════════════════════════════════
  errorBoundaryFallbackMessage: {
    en: 'An unexpected error occurred.',
    he: 'אירעה שגיאה בלתי צפויה.',
  },
  errorBoundaryTryAgain: { en: 'Try again', he: 'נסה שוב' },

  // ═══════════════════════════════════════════════════════════════════════
  //  SaveErrorBanner
  // ═══════════════════════════════════════════════════════════════════════
  saveErrorCopyAria: { en: 'Copy diagnostics', he: 'העתק פרטי אבחון' },
  saveErrorCopied: { en: 'Copied ✓', he: 'הועתק ✓' },
  saveErrorCopy: { en: 'Copy', he: 'העתק' },
  saveErrorCopyPrompt: {
    en: 'Copy this diagnostic info:',
    he: 'העתק את פרטי האבחון הבאים:',
  },
  saveErrorReferenceLabel: { en: 'Reference', he: 'מספר אסמכתא' },

  // ═══════════════════════════════════════════════════════════════════════
  //  DataTable
  // ═══════════════════════════════════════════════════════════════════════
  dataTableSearchPlaceholder: { en: 'Search...', he: 'חיפוש...' },
  dataTableSearchAria: { en: 'Search table', he: 'חיפוש בטבלה' },
  dataTableNoData: { en: 'No data available', he: 'אין נתונים זמינים' },
  dataTablePageOf: { en: 'Page {n} of {m}', he: 'עמוד {n} מתוך {m}' },
  dataTablePrevAria: { en: 'Previous page', he: 'העמוד הקודם' },
  dataTablePrev: { en: 'Previous', he: 'הקודם' },
  dataTableNextAria: { en: 'Next page', he: 'העמוד הבא' },
  dataTableNext: { en: 'Next', he: 'הבא' },

  // ═══════════════════════════════════════════════════════════════════════
  //  LoadingSpinner
  // ═══════════════════════════════════════════════════════════════════════
  loadingSpinnerDefault: { en: 'Loading...', he: 'טוען...' },

  // ═══════════════════════════════════════════════════════════════════════
  //  StringListEditor
  // ═══════════════════════════════════════════════════════════════════════
  stringListPlaceholder: { en: 'Type and press Enter…', he: 'הקלד ולחץ Enter…' },
  stringListNone: { en: 'None', he: 'ללא' },
  stringListAdd: { en: 'Add', he: 'הוסף' },
  stringListRemove: { en: 'Remove "{item}"', he: 'הסר "{item}"' },

  // ═══════════════════════════════════════════════════════════════════════
  //  loginErrorMessage util
  // ═══════════════════════════════════════════════════════════════════════
  loginErrNetwork: {
    en: 'Cannot reach the server. Please check that the backend is running and try again.',
    he: 'לא ניתן להגיע לשרת. ודא שהשרת פועל ונסה שוב.',
  },
  loginErrLockedMinutesOne: {
    en: 'This account is temporarily locked after too many failed attempts. Try again in about {n} minute, or ask an admin to unlock it.',
    he: 'החשבון נחסם זמנית לאחר יותר מדי ניסיונות כושלים. נסה שוב בעוד כ-{n} דקה, או פנה למנהל כדי לשחרר אותו.',
  },
  loginErrLockedMinutesMany: {
    en: 'This account is temporarily locked after too many failed attempts. Try again in about {n} minutes, or ask an admin to unlock it.',
    he: 'החשבון נחסם זמנית לאחר יותר מדי ניסיונות כושלים. נסה שוב בעוד כ-{n} דקות, או פנה למנהל כדי לשחרר אותו.',
  },
  loginErrLockedGeneric: {
    en: 'This account is temporarily locked after too many failed attempts. Try again later or ask an admin to unlock it.',
    he: 'החשבון נחסם זמנית לאחר יותר מדי ניסיונות כושלים. נסה שוב מאוחר יותר או פנה למנהל כדי לשחרר אותו.',
  },
  loginErrInvalid: {
    en: 'Invalid email or password. Please try again.',
    he: 'אימייל או סיסמה שגויים. נסה שוב.',
  },
  loginErrForbidden: {
    en: 'Account locked or suspended. Contact your administrator.',
    he: 'החשבון נחסם או הושעה. פנה למנהל המערכת.',
  },
  loginErrServer: {
    en: 'Server error. Please try again in a moment.',
    he: 'שגיאת שרת. נסה שוב בעוד רגע.',
  },
  loginErrHttp: {
    en: 'Login failed (HTTP {n}). Please try again.',
    he: 'ההתחברות נכשלה (HTTP {n}). נסה שוב.',
  },
  loginErrGeneric: {
    en: 'Login failed. Please try again.',
    he: 'ההתחברות נכשלה. נסה שוב.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  apiErrorMessage util
  // ═══════════════════════════════════════════════════════════════════════
  apiErrUnreachable: {
    en: 'The server is unreachable. Check your connection and try again.',
    he: 'השרת אינו זמין. בדוק את החיבור ונסה שוב.',
  },
  apiErrSessionExpired: {
    en: 'Your session has expired. Please sign in again.',
    he: 'תוקף ההתחברות פג. אנא התחבר מחדש.',
  },
  apiErrPermissionDenied: {
    en: "You don't have permission to do that. Contact your administrator.",
    he: 'אין לך הרשאה לבצע פעולה זו. פנה למנהל המערכת.',
  },
  apiErrNotFound: {
    en: 'The requested item could not be found.',
    he: 'הפריט המבוקש לא נמצא.',
  },
  apiErrLicenseLimit: {
    en: "User license limit reached — raise the tenant's license in Tenants > Edit.",
    he: 'הגעת למגבלת רישיונות המשתמשים — הגדל את רישיון הארגון בארגונים > עריכה.',
  },
  apiErrConflict: {
    en: 'That conflicts with the current state. Refresh and try again.',
    he: 'הפעולה מתנגשת עם המצב הנוכחי. רענן ונסה שוב.',
  },
  // Frontend-owned, bilingual copy for specific backend guard codes that are
  // safe and useful to show verbatim (e.g. "you can't remove your own admin
  // role"). Never pass the backend's own message text through directly — it's
  // English-only prose and would break language support; these keys are the
  // translated equivalent, matched by machine code, not by string content.
  apiErrSelfLockout: {
    en: "You can't make this change to your own account.",
    he: 'לא ניתן לבצע שינוי זה בחשבון שלך.',
  },
  apiErrLastAdminForbidden: {
    en: 'This would leave the organization with no administrator. Assign another admin first.',
    he: 'פעולה זו תשאיר את הארגון ללא מנהל. הגדר מנהל נוסף לפני שתמשיך.',
  },
  apiErrAlreadyVerified: {
    en: 'This account is already verified.',
    he: 'החשבון הזה כבר מאומת.',
  },
  apiErrEmailExists: {
    en: 'A user with this email already exists.',
    he: 'כבר קיים משתמש עם אימייל זה.',
  },
  apiErrInvalidInput: {
    en: 'Some of the information you entered is invalid.',
    he: 'חלק מהפרטים שהזנת אינם תקינים.',
  },
  apiErrServer: {
    en: 'The server ran into a problem. Please try again shortly.',
    he: 'השרת נתקל בבעיה. נסה שוב בקרוב.',
  },
  apiErrGeneric: {
    en: 'Something went wrong. Please try again.',
    he: 'משהו השתבש. נסה שוב.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  loadErrorMessage util — generic, user-safe copy only. No backend
  //  service name, port, or raw HTTP status is ever shown here — that detail
  //  goes to console.error (and, via the request's transactionId, to the
  //  audit trail) for support/ops to trace. See Output/Runbooks/
  //  tx-log-investigation.md.
  // ═══════════════════════════════════════════════════════════════════════
  loadErrUnauthorized: {
    en: 'Not authorized. Please sign in again.',
    he: 'אין הרשאה. אנא התחבר מחדש.',
  },
  loadErrNotFound: {
    en: "We couldn't find {resource}. Please try again, or contact support if this keeps happening.",
    he: 'לא הצלחנו למצוא את {resource}. נסה שוב, ואם זה חוזר על עצמו פנה לתמיכה.',
  },
  loadErrUnavailable: {
    en: "This isn't available right now. Please try again in a few minutes.",
    he: 'זה לא זמין כרגע. נסה שוב בעוד כמה דקות.',
  },
  loadErrFailed: {
    en: 'Something went wrong loading {resource}. Please try again, and contact support if this keeps happening.',
    he: 'משהו השתבש בטעינת {resource}. נסה שוב, ואם זה חוזר על עצמו פנה לתמיכה.',
  },
  loadErrGeneric: {
    en: 'Failed to load {resource}. Please try again.',
    he: 'טעינת {resource} נכשלה. נסה שוב.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  saveErrorMessage util (unauthorized/unavailable reuse the loadErr* keys
  //  above — identical EN text). Same "generic only" rule as loadErrorMessage.
  // ═══════════════════════════════════════════════════════════════════════
  saveErrGeneric: {
    en: 'Failed to save {resource}.',
    he: 'שמירת {resource} נכשלה.',
  },
  saveErrValidation: {
    en: 'Validation failed for {resource}.',
    he: 'האימות של {resource} נכשל.',
  },
  saveErrHttp: {
    en: "We couldn't save {resource}. Please try again.",
    he: 'לא הצלחנו לשמור את {resource}. נסה שוב.',
  },
  saveErrFailed: {
    en: 'Something went wrong saving {resource}. Please try again, and contact support if this keeps happening.',
    he: 'משהו השתבש בשמירת {resource}. נסה שוב, ואם זה חוזר על עצמו פנה לתמיכה.',
  },
} satisfies Record<string, { en: string; he: string }>;
